// Vehicle reservation ledger + assignment mutex.
//
// WHY THIS EXISTS (TC-02 / TC-03)
// -------------------------------
// Assigning a forklift is two separate network round-trips:
//
//   1. AdminFindNearestDriver  — reads vehicles, reads active jobs, asks
//                                GetVehicleAlreadyInfo "is this vehicle free?"
//   2. AdminAcceptBooking      — POSTs PostForkBooking to claim it
//
// Nothing holds the vehicle between (1) and (2). Two requests handled close
// together both read "FREE" for the same forklift and both claim it — a
// classic time-of-check-to-time-of-use race. TC-03 is the worst version:
// two requests from the SAME pickup at the SAME time produce an identical
// ranking, so they deterministically pick the identical winner.
//
// The gap is not small. In the auto-accept path there is a poll loop of up to
// 15 seconds between ranking and claiming; in the manual modal path the admin
// can sit on an open dialog indefinitely.
//
// This module closes the window by recording an intent-to-assign the moment a
// vehicle is chosen, so any ranking that runs afterwards already excludes it —
// without waiting for the backend to catch up.
//
// SCOPE — READ THIS
// -----------------
// This ledger lives in ONE browser tab's memory. It fully serialises assignment
// within a single admin console, which is where TC-02/TC-03 are actually
// exercised. It CANNOT stop two different devices (two admin browsers, or a
// driver device running the TC-09 reassign flow) from racing each other.
// Cross-device safety requires the backend to reject the second write — a
// uniqueness constraint on (vehicle, overlapping time slot), or a conditional
// assign endpoint. Until that exists, `revalidate` below narrows the remaining
// cross-device window but does not eliminate it.

const TTL_MS = 5 * 60 * 1000; // a reservation this old is treated as abandoned

/** @type {Map<string, {vehicleId:string,start:number,end:number,bookingId:string,at:number}>} */
const reservations = new Map();

let seq = 0;

const parseTime = (value) => {
  if (value == null) return NaN;
  const d = value instanceof Date ? value : new Date(value);
  const t = d.getTime();
  return Number.isNaN(t) ? NaN : t;
};

const prune = () => {
  const cutoff = Date.now() - TTL_MS;
  for (const [key, r] of reservations) {
    if (r.at < cutoff) reservations.delete(key);
  }
};

// Half-open overlap: touching intervals (one ends exactly as the next begins)
// do NOT conflict, which matches how GetVehicleAlreadyInfo treats them.
const overlaps = (aStart, aEnd, bStart, bEnd) => aStart < bEnd && bStart < aEnd;

/**
 * Vehicle ids already spoken for during [start, end).
 * Feed straight into AdminFindNearestDriver's `excludeVehicleIds`.
 *
 * `ignoreBookingId` lets a booking re-rank without colliding with its own
 * in-flight reservation (the TC-09 reassign path re-ranks the same booking).
 */
export const getReservedVehicleIds = (start, end, { ignoreBookingId } = {}) => {
  prune();
  const s = parseTime(start);
  const e = parseTime(end);
  // Unparseable window — fail safe by excluding every live reservation rather
  // than silently excluding none.
  const unbounded = Number.isNaN(s) || Number.isNaN(e);
  const ignore = ignoreBookingId != null ? String(ignoreBookingId) : null;

  const out = new Set();
  for (const r of reservations.values()) {
    if (ignore && r.bookingId === ignore) continue;
    if (unbounded || overlaps(s, e, r.start, r.end)) out.add(r.vehicleId);
  }
  return Array.from(out);
};

/** Is this specific vehicle already claimed for an overlapping slot? */
export const isVehicleReserved = (vehicleId, start, end, { ignoreBookingId } = {}) => {
  if (!vehicleId) return false;
  return getReservedVehicleIds(start, end, { ignoreBookingId })
    .includes(String(vehicleId).trim());
};

/**
 * Claim a vehicle for [start, end). Returns a release handle, or null when the
 * vehicle is already claimed — callers MUST treat null as "pick another one".
 *
 * Release on both success and failure: on success the backend becomes the
 * source of truth and the ledger entry is redundant; on failure the vehicle was
 * never actually taken and must not stay blocked.
 */
export const reserveVehicle = (vehicleId, start, end, bookingId) => {
  if (!vehicleId) return null;
  const id = String(vehicleId).trim();
  const bId = bookingId != null ? String(bookingId) : '';

  if (isVehicleReserved(id, start, end, { ignoreBookingId: bId })) return null;

  const s = parseTime(start);
  const e = parseTime(end);
  const key = `${id}::${bId}::${++seq}`;
  reservations.set(key, {
    vehicleId: id,
    // An unparseable window still reserves — it just blocks any overlap check
    // (see `unbounded` above) rather than being silently ignored.
    start: Number.isNaN(s) ? -Infinity : s,
    end: Number.isNaN(e) ? Infinity : e,
    bookingId: bId,
    at: Date.now(),
  });

  return {
    vehicleId: id,
    release: () => reservations.delete(key),
  };
};

/** Drop every reservation held for a booking (e.g. it was cancelled). */
export const releaseBooking = (bookingId) => {
  if (bookingId == null) return;
  const bId = String(bookingId);
  for (const [key, r] of reservations) {
    if (r.bookingId === bId) reservations.delete(key);
  }
};

/** Test seam. */
export const _clearAllReservations = () => reservations.clear();

// ─── Assignment mutex ────────────────────────────────────────────────────────
// Serialises whole find→claim sequences so the auto-accept loop and a manually
// confirmed modal cannot interleave. The ledger alone protects the choice of
// vehicle; this additionally stops two full flows running concurrently and
// both burning the same 15-second ranking snapshot.

let chain = Promise.resolve();

export const withAssignmentLock = (fn) => {
  const run = chain.then(fn, fn);
  // Keep the chain alive even when a caller rejects, so one failed assignment
  // does not wedge every later one.
  chain = run.then(() => undefined, () => undefined);
  return run;
};
